import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { ScoreFormat } from '../../_shared/models/score-format.model';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { AuthService } from '../../auth/auth.service';

@Component({
  standalone: false,
    selector: 'app-match-format-form',
    templateUrl: './match-format-form.component.html',
    styleUrls: ['./match-format-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchFormatFormComponent implements OnChanges {
    authService = inject(AuthService);

    @Input() matchFormat = 'SET3-S:6/TB7@6';
    @Input() discipline: DisciplineEnum;
    @Input() setsLength = 0;
    @Input() dividerTitle = 'Score format';
    @Output() updatedMatchFormat = new EventEmitter();
    resetLastSet = false;

    form: FormGroup;
    scoreFormat: ScoreFormat;

    constructor(
        private fb: FormBuilder,
        private boardHelpService: BoardHelpService
    ) { }

    ngOnChanges() {
        this.buildForm();
    }

    buildForm() {
        if (!this.matchFormat) this.matchFormat = 'SET3-S:6/TB7@6';

        this.scoreFormat = this.boardHelpService.getScoreFormat(this.matchFormat, this.discipline, null, true);

        this.form = this.fb.group({
            setType: [{ value: this.scoreFormat.setType, disabled: this.setsLength > 1 }],
            ads: [this.scoreFormat.ads],
            numberOfSets: [{ value: String(this.scoreFormat.numberOfSets), disabled: this.setsLength > 1 }],
            numberOfGames: [{ value: String(this.scoreFormat.numberOfGamesInSet), disabled: this.setsLength > 1 }],
            lastSet: [{ value: this.scoreFormat.lastSet, disabled: false }],
            tiebreakAt: [{ value: String(this.scoreFormat.tiebreakAt), disabled: this.setsLength > 1 }],
            tiebreakType: [{ value: String(this.scoreFormat.tiebreakType), disabled: this.setsLength > 1 }],
            numberOfMinutes: [10, Validators.pattern(/^[1-9]\d*$/)],
            gamesOrPoints: ['P'],
        });
        // If lastSet is TB10@6, manually trigger the logic to disable controls
        if (this.scoreFormat.lastSet === 'TB10@6') {
            const gamesCtrl = this.form.get('numberOfGames');
            const tiebreakAtCtrl = this.form.get('tiebreakAt');
            const tiebreakTypeCtrl = this.form.get('tiebreakType');
            
            gamesCtrl?.setValue('6');
            tiebreakAtCtrl?.setValue('6');
            tiebreakTypeCtrl?.setValue('TB7');
            gamesCtrl?.disable();
            tiebreakAtCtrl?.disable();
            tiebreakTypeCtrl?.disable();
        }

        // Set default value on when is tiebreak played at
        this.form
            .get('numberOfGames')
            .valueChanges
            .subscribe(
                value => {
                    switch (value) {
                        case '4':
                            this.form.get('tiebreakAt').setValue('3');
                            // this.form.get('numberOfSets').setValue('3');
                            break;
                        case '5':
                            this.form.get('tiebreakAt').setValue('4');
                            // this.form.get('numberOfSets').setValue('1');
                            break;
                        case '6':
                            this.form.get('tiebreakAt').setValue('6');
                            // this.form.get('numberOfSets').setValue('3');
                            break;
                        case '7':
                            this.form.get('tiebreakAt').setValue('6');
                            // this.form.get('numberOfSets').setValue('1');
                            break;
                        case '8':
                            this.form.get('tiebreakAt').setValue('7');
                            // this.form.get('numberOfSets').setValue('1');
                            break;
                        case '9':
                            this.form.get('tiebreakAt').setValue('8');
                            // this.form.get('numberOfSets').setValue('1');
                            break;
                        case '10':
                            this.form.get('tiebreakAt').setValue('10');
                            // this.form.get('numberOfSets').setValue('1');
                            break;
                        case '16':
                            this.form.get('tiebreakAt').setValue('15');
                            // this.form.get('numberOfSets').setValue('1');
                            break;
                        default:
                            break;
                    }

                }
            );

        // TODO numberOfSets === 1 lastSet = regular
        this.form
            .get('numberOfSets')
            .valueChanges
            .subscribe(
                value => {
                    if (value === '1') {
                        this.form.get('lastSet').setValue('');
                    }
                }
            );

        this.form
            .get('lastSet')
            .valueChanges
            .subscribe(
                async value => {
                    if (this.setsLength === this.scoreFormat.numberOfSets) {
                        this.resetLastSet = true;
                    }
                    if (!this.form) return;
                    const gamesCtrl = this.form.get('numberOfGames');
                    const tiebreakAtCtrl = this.form.get('tiebreakAt');
                    const tiebreakTypeCtrl = this.form.get('tiebreakType');
                    if (value === 'TB10@6') {
                        gamesCtrl?.setValue('6');
                        tiebreakAtCtrl?.setValue('6');
                        tiebreakTypeCtrl?.setValue('TB7');
                        gamesCtrl?.disable();
                        tiebreakAtCtrl?.disable();
                        tiebreakTypeCtrl?.disable();
                    } else {
                        if (!this.setsLength || this.setsLength <= 1) {
                            gamesCtrl?.enable();
                            tiebreakAtCtrl?.enable();
                            tiebreakTypeCtrl?.enable();
                        }
                    }
                }
            );

        this.form
            .get('setType')
            .valueChanges
            .subscribe(
                async value => {
                    if (value === 'TIMED') {
                        this.form.controls.numberOfSets.setValue('1');
                        this.form.controls.numberOfMinutes.setValue(10);
                    } else if (value === 'STANDARD') {
                        this.form.controls.numberOfSets.setValue('3');
                        this.form.controls.numberOfGames.setValue('6');
                        this.form.controls.tiebreakAt.setValue('6');
                        this.form.controls.tiebreakType.setValue('TB7');
                        this.form.controls.numberOfMinutes.setValue(null);
                    } else {
                        this.form.controls.numberOfSets.setValue('1');
                        this.form.controls.tiebreakType.setValue('TB10');
                    }
                }
            );

        this.form
            .get('gamesOrPoints')
            .valueChanges
            .subscribe(
                async value => {
                    this.form.controls.ads.setValue(true);
                }
            );

        this.form
            .valueChanges
            .subscribe(
                () => {
                    this.updatedMatchFormat.emit(this.getMatchFormat());
                }
            );

        this.updatedMatchFormat.emit(this.getMatchFormat())
    }

    /**
     * 
     * @param event 
     * This will be called when the user changes the number of games per set
     * It will auto-set the number of sets based on the number of games per set
     */
    onNumberOfGamesChange(event: any) {
        // You can add your auto-setting logic here    
        this.autoSetNumberOfSets(event.detail.value);    
    }    

    private getMatchFormat() {
        const formValue = this.form.getRawValue();

        const sets = 'SET' + formValue.numberOfSets;
        let games = '';
        let lastSet = '';
        let timed = ''
        if (formValue.setType === 'STANDARD') {
            games = `-S:${formValue.numberOfGames}${formValue.ads ? '' : 'NOAD'}/${formValue.tiebreakType}@${formValue.tiebreakAt}`;

            lastSet = '';
            if (formValue.lastSet === 'WB2') {
                lastSet = `-F:${formValue.numberOfGames}`;
            } else if (formValue.lastSet === 'TB10@6') {
                games = `-S:6/TB7@6`;
                lastSet = `-F:6/TB10@6`;
            }else {
                lastSet = `${formValue.lastSet ? '-F:' + formValue.lastSet : ''}`;
            }

        } else if (formValue.setType === 'TIEBREAK') {
            games = `-S:${formValue.tiebreakType}`;
        } else {
            // TODO Handle NOAD
            timed = `-S:${formValue.numberOfMinutes}${formValue.gamesOrPoints}${formValue.ads ? '' : 'NOAD'}`
        }
        const result = sets + games + lastSet + timed;
        return result
    }

    // Header for action sheet
    customOptions(header) {
        return { header };
    }

    showLastSet() {
        if (this.form.get('numberOfSets').value > 1) {
            return true;
        }
    }

    private autoSetNumberOfSets(tiebreakValue: string) {
        const gamesValue = this.form.get('numberOfGames').value;

        switch (tiebreakValue) {
            case '3':
                if (gamesValue === '4') {
                    this.form.get('numberOfSets').setValue('3');
                }
                break;
            case '4':
                if (gamesValue === '5') {
                    this.form.get('numberOfSets').setValue('1');
                }
                break;
            case '6':
                if (gamesValue === '6') {
                    this.form.get('numberOfSets').setValue('3');
                } else if (gamesValue === '7') {
                    this.form.get('numberOfSets').setValue('1');
                }
                break;
            case '7':
                if (gamesValue === '8') {
                    this.form.get('numberOfSets').setValue('1');
                }
                break;
            case '8':
                if (gamesValue === '8' || gamesValue === '9') {
                    this.form.get('numberOfSets').setValue('1');
                }
                break;
            case '10':
                if (gamesValue === '10') {
                    this.form.get('numberOfSets').setValue('1');
                }
                break;
            case '15':
                if (gamesValue === '16') {
                    this.form.get('numberOfSets').setValue('1');
                }
                break;
            default:
                break;
        }

    }


}
